import { recordEventWithCheck } from "src/apiService/modules/events";
import ServiceClient from "src/utils/extension/ServiceClient";
import { getMercariCookies } from "src/utils/extension/mercari";

/*
async function sha256(et) {
    const en = new TextEncoder().encode(et);
    const ed = await window.crypto.subtle.digest("SHA-256", en);
    return Array.from(new Uint8Array(ed)).map(et => et.toString(16).padStart(2, "0")).join("")
}

async function getPersistedQuerySha256Hash(queryDefinition) {
    return await sha256(require('graphql/language').print(queryDefinition))
}
*/

export class NotLoggedError extends Error {
  constructor() {
    super("You must be logged in Mercari to perform the sync process.");
  }
}

export class UnauthorizedError extends Error {
  url: string;
  status: number;
  json: object;

  constructor(operation: string, url: string, status: number, json: object) {
    super(`(mercari) ${operation} Failed (unauthorized error property)`);
    this.url = url;
    this.status = status;
    this.json = json;
  }
}

const MercariURL = "https://www.mercari.com";

export interface UserItemsQueryParameters {
  status?: "sold_out" | "on_sale" | "trading";
  sortBy?: "created" | "updated" | "name" | "price" | "num_likes";
  sortType?: "asc" | "desc";
  page?: number;
}

export const enum MercariItemStatus {
  ForSale = 1,
  InProgress = 2,
  Complete = 3,
}

export const enum MercariSortBy {
  NewestListed = 2,
  LowestPrice = 3,
  HighestPrice = 4,
}

function buildURL(
  base: string,
  path: string,
  searchParams: Record<string, string> = {}
): string {
  const url = new URL(path, base);

  for (const [key, value] of Object.entries(searchParams)) {
    url.searchParams.set(key, value);
  }

  return url.toString();
}

function isPersistedQueryNotFound(json: any): boolean {
  return (
    Array.isArray(json?.errors) &&
    json.errors.some(
      (e: any) =>
        e?.message === "PersistedQueryNotFound" ||
        e?.extensions?.code === "PERSISTED_QUERY_NOT_FOUND"
    )
  );
}

/** Only one mercari-persisted-query-not-found Event per page load. */
let persistedQueryNotFoundEventLogged = false;

function recordPersistedQueryNotFound(operation: string) {
  if (persistedQueryNotFoundEventLogged) return;
  persistedQueryNotFoundEventLogged = true;

  void recordEventWithCheck({
    type: "mercari-persisted-query-not-found",
    operation,
    message: `(mercari) ${operation} PersistedQueryNotFound`,
  }).catch((err) => {
    console.warn(
      "(mercari) failed to record mercari-persisted-query-not-found event",
      err
    );
  });
}

function checkErrorsProperty(
  operation: string,
  url: string,
  status: number,
  json: any
) {
  if (json.errors) {
    if (json.data) {
      console.warn(
        `(mercari) ${operation} warn (errors property, but we've got data)`,
        {
          url,
          status,
          json,
        }
      );
    } else {
      console.error(`(mercari) ${operation} failed (errors property)`, {
        url,
        status,
        json,
      });

      if (isPersistedQueryNotFound(json)) {
        recordPersistedQueryNotFound(operation);
      }

      if (
        Array.isArray(json.errors) &&
        json.errors.some(
          (e) => e.status === 401 || e.message === "Unauthorized"
        )
      )
        throw new UnauthorizedError(operation, url, status, json);
      throw new Error(`(mercari) ${operation} Failed (errors property)`);
    }
  }
}

class MercariClient extends ServiceClient {
  async getCredentials(): Promise<{ accessToken: string; userId: string }> {
    const cookies = await getMercariCookies(this.extensionId);
    const mwus = cookies.find((c) => c.name === "_mwus")?.value;
    if (!mwus) throw new NotLoggedError();
    let payload: { accessToken: string; userId: string };
    try {
      payload = JSON.parse(atob(mwus)); // accessToken, refreshToken, csrfSecret, userId
    } catch (e) {
      throw new Error("Invalid poshmark JWT", e as Error);
    }

    if (!payload.userId) throw new NotLoggedError();
    return payload;
  }

  // The web sends this as a POST request
  // 1. Go to: Complete ( https://www.mercari.com/mypage/listings/complete/?page=1 ) -> Select Any -> View Order
  //    e.g.: https://www.mercari.com/transaction/order_status/m60269131132/
  // 2. Search for the /v1/api call that uses the `OrderStatusConsolidated` operationName
  async orderStatusConsolidated(referenceId: string) {
    const credentials = await this.getCredentials();
    const url = buildURL(MercariURL, "/v1/api", {
      operationName: "OrderStatusConsolidated",
      variables: JSON.stringify({
        input: {
          referenceId,
          referenceType: "item",
        },
      }),
      extensions: JSON.stringify({
        persistedQuery: {
          version: 1,
          sha256Hash:
            "1e2de463bdf2fb33c7c328ad2659d4ef20ace0e4ff0c526b64d6502b26c23f6a",
        },
      }),
    });
    const res = await this.fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${credentials.accessToken}`,
        "X-Platform": "web",
      },
    });

    if (!res.ok) {
      console.error(res);
      console.error("(mercari) orderStatusConsolidated failed (status code)", {
        url,
        body: res.data,
        status: res.status,
      });

      throw new Error("(mercari) orderStatusConsolidated Failed (status code)");
    }

    const json = res.data;
    checkErrorsProperty("orderStatusConsolidated", url, res.status, json);

    return json.data.orderStatus.contents;
  }

  // 1. Go to: https://www.mercari.com/mypage/listings/active/
  // 2. Search for the `v1/api` call that uses the operationName `userItemsQuery`
  async userItemsQuery({
    status = "sold_out",
    sortBy = "created",
    sortType = "desc",
    page = 1,
  }: UserItemsQueryParameters = {}) {
    const credentials = await this.getCredentials();
    const url = buildURL(MercariURL, "/v1/api", {
      operationName: "userItemsQuery",
      variables: JSON.stringify({
        userItemsInput: {
          sellerId: credentials.userId,
          status,
          sortBy,
          sortType,
          page,
        },
      }),
      extensions: JSON.stringify({
        persistedQuery: {
          version: 1,
          sha256Hash:
            "88c1f24f1ee3617c5e4b04f33d4f9aec7357e55cb94fea8affc78571df6368f3",
        },
      }),
    });
    const res = await this.fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${credentials.accessToken}`,
      },
    });

    if (!res.ok) {
      console.error(res);
      console.error("(mercari) userItemsQuery failed (status code)", {
        url,
        body: res.data,
        status: res.status,
      });

      throw new Error("(mercari) userItemsQuery Failed (status code)");
    }

    const json = res.data;
    checkErrorsProperty("userItemsQuery", url, res.status, json);

    if (!json.data.userItems) {
      console.error(
        "(mercari) userItemsQuery failed (no data.userItems property)",
        {
          url,
          json,
        }
      );

      throw new Error(
        "(mercari) userItemsQuery Failed (no data.userItems property)"
      );
    }

    return json.data.userItems;
  }

  async *userItemsQueryFull(args?: Omit<UserItemsQueryParameters, "page">) {
    for (let page = 1; ; page++) {
      const res = await this.userItemsQuery({ ...args, page });

      yield {
        sales: res.items,
        pagination: res.pagination,
      };

      if (!res.pagination.hasNext) break;
    }
  }

  // 1. Go to any item: e.g.: https://www.mercari.com/us/item/m60269131132/
  // 2. Search for the /v1/api call that uses the `productQuery` operationName
  async productQuery(id: string) {
    const credentials = await this.getCredentials();
    const url = buildURL(MercariURL, "/v1/api", {
      operationName: "productQuery",
      variables: JSON.stringify({
        id,
      }),
      extensions: JSON.stringify({
        persistedQuery: {
          version: 1,
          sha256Hash:
            "daea89a107980422b75c340354be99fddec4218262d09fb05c4ef6c19831a858",
        },
      }),
    });
    const res = await this.fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${credentials.accessToken}`,
      },
    });

    if (!res.ok) {
      console.error(res);
      console.error("(mercari) productQuery failed (status code)", {
        url,
        body: res.data,
        status: res.status,
        id,
      });

      throw new Error("(mercari) productQuery Failed (status code)");
    }

    const json = res.data;
    checkErrorsProperty("productQuery", url, res.status, json);

    return json.data.item;
  }

  // 1. Go to any user profile: e.g: https://www.mercari.com/u/user292277823/
  // 2. Search for the /v1/api call that uses the `publicProfileSearchQuery` operationName
  async publicProfileSearchQuery({
    sellerId,
    length = 100,
    offset = 0,
    itemStatuses = [MercariItemStatus.ForSale],
    query = "",
    sortBy = MercariSortBy.NewestListed,
  }: {
    sellerId: number;
    length?: number;
    offset?: number;
    itemStatuses?:
      | [MercariItemStatus.ForSale]
      | [MercariItemStatus.InProgress, MercariItemStatus.Complete] // Sold
      | [
          MercariItemStatus.ForSale,
          MercariItemStatus.InProgress,
          MercariItemStatus.Complete
        ];
    query?: string;
    sortBy?: MercariSortBy;
  }) {
    const credentials = await this.getCredentials();
    const url = buildURL(MercariURL, "/v1/api", {
      operationName: "publicProfileSearchQuery",
      variables: JSON.stringify({
        criteria: {
          sellerIds: [sellerId],
          offset,
          itemStatuses,
          query,
          sortBy,
          facets: [1, 2, 3, 9, 18],
          hashtags: [],
        },
        userId: sellerId,
      }),
      extensions: JSON.stringify({
        persistedQuery: {
          version: 1,
          sha256Hash:
            "41c93e8711bcf67fd9b79c9cd984af376b8339496a74d6f42a8063302532d335",
        },
      }),
    });
    const res = await this.fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${credentials.accessToken}`,
      },
    });

    if (!res.ok) {
      console.error(res);
      console.error("(mercari) publicProfileSearchQuery failed (status code)", {
        url,
        body: res.data,
        status: res.status,
      });

      throw new Error(
        "(mercari) publicProfileSearchQuery Failed (status code)"
      );
    }

    const json = res.data;
    checkErrorsProperty("publicProfileSearchQuery", url, res.status, json);

    return json.data.search; // { itemList, page: { count } }
  }

  async *publicProfileSearchQueryFull() {
    let offset = 0;
    let count = 0;
    const sellerId = parseInt((await this.getCredentials()).userId, 10) || -1;

    do {
      const res = await this.publicProfileSearchQuery({ sellerId, offset });
      count = res.count;

      if (!res?.itemsList?.length) return;

      yield {
        items: res.itemsList,
        pagination: {
          count,
          offset,
        },
      };

      offset += res.itemsList.length;
    } while (offset < count);
  }

  async initialize() {
    const url = buildURL(MercariURL, "/v1/initialize");
    const res = await this.fetch(url);
    if (!res.ok) {
      console.error(res);
      console.error("(mercari) initialize failed (status code)", {
        url,
        body: res.data,
        status: res.status,
      });

      throw new Error("(mercari) initialize Failed (status code)");
    }

    const json = res.data;
    checkErrorsProperty("initialize", url, res.status, json);
    return json;
  }
}

export default MercariClient;
